import React, { Fragment, useEffect, useState } from 'react';
import { useTranslate, TextInput, required, SelectInput, FormDataConsumer } from 'react-admin';
import { useSelector } from 'react-redux';
import {
  AutocompleteArrayTextInput,
  DateInput,
  HiddenInput,
  KycLevelInput,
  ReferenceInput,
} from '~/components/ra';
import { selectors } from '~/ducks/context';
import { Pricing } from '~/types';
import { Alert, Skeleton } from '@material-ui/lab';
import { useGetAllProfileTags } from '~/hooks/profileTags';
import { useExternalSourceTypes } from '~/hooks/externalSources';
import { useForm } from 'react-final-form';

interface Props {
  pricingRecord?: Pricing;
  disabled?: boolean;
  edit?: boolean;
  format?: string;
  create?: boolean;
}

const PricingForm = (props: Props) => {
  const { pricingRecord, disabled = false, edit = false, format } = props;

  const [instrument, setInstrument] = useState<string>();

  const handleInstrumentChange = (e: any) => {
    setInstrument(e.target.value);
  };

  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.solutions.pricings.${key}`);

  const solutionId = useSelector(selectors.get('solutionId'));

  const { data: externalSourceTypesData, isLoading } = useExternalSourceTypes();
  const externalSourceTypes = isLoading
    ? []
    : externalSourceTypesData?.map((type) => ({ id: type, name: type }));

  const { data: allTags, isLoading: allTagsLoading } = useGetAllProfileTags();
  const autocompleteOptions = allTags?.map((tag) => tag?.name);

  const form = useForm();
  const paymentInstrumentValue = form.getFieldState('paymentInstrument')?.value;
  useEffect(() => {
    if (edit && !paymentInstrumentValue && pricingRecord) {
      const rafId = requestAnimationFrame(() => {
        const paymentInstrument = pricingRecord.productId ? 'product' : 'externalSourceType';
        form.change('paymentInstrument', paymentInstrument);
        setInstrument(paymentInstrument);
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [edit, form, paymentInstrumentValue, pricingRecord]);

  return (
    <FormDataConsumer>
      {() => {
        return (
          <Fragment>
            {!edit && <HiddenInput source='solutionId' disabled />}
            <HiddenInput source='data' disabled />
            {pricingRecord?.format === 'v2' || format === 'v2' ? (
              <>
                <HiddenInput source='format' defaultValue='v2' disabled />
                <TextInput
                  source='name'
                  label={t('name')}
                  fullWidth
                  disabled={disabled}
                  validate={required()}
                />
                <SelectInput
                  source='paymentInstrument'
                  label={t('paymentInstrument')}
                  onChange={handleInstrumentChange}
                  choices={[
                    { id: 'product', name: t('product') },
                    { id: 'externalSourceType', name: t('externalSourceType') },
                  ]}
                  fullWidth
                  validate={required()}
                />
                {instrument === 'product' && (
                  <ReferenceInput
                    label={t('product')}
                    source='productId'
                    reference='products'
                    filter={{ solutionId }}
                  >
                    <SelectInput optionText='name' validate={required()} />
                  </ReferenceInput>
                )}
                {instrument === 'externalSourceType' && (
                  <SelectInput
                    source='externalSourceType'
                    label={t('externalSourceType')}
                    choices={externalSourceTypes}
                    fullWidth
                    validate={required()}
                  />
                )}
                <DateInput
                  source='activeFrom'
                  label={t('activeFrom')}
                  options={{ format: 'LL', clearable: true }}
                  fullWidth
                  disabled={disabled}
                />
                <DateInput
                  source='activeTill'
                  label={t('activeTill')}
                  options={{ format: 'LL', clearable: true }}
                  fullWidth
                  disabled={disabled}
                />
                <SelectInput
                  source='accountStatus'
                  label={t('profileStatus')}
                  choices={[
                    { id: 'ACTIVE', name: t('Active') },
                    { id: 'BLOCKED', name: t('Blocked') },
                    { id: 'CLOSED', name: t('Closed') },
                  ]}
                  fullWidth
                  disabled={disabled}
                  allowEmpty
                  emptyText={t('any')}
                />
                <KycLevelInput
                  source='kycLevel'
                  label={t('profileKycLevel')}
                  fullWidth
                  disabled={disabled}
                  allowEmpty
                />
                {allTagsLoading ? (
                  <Skeleton width='100%' />
                ) : (
                  <AutocompleteArrayTextInput
                    record={pricingRecord}
                    source='profileTags'
                    label={t('profileTags')}
                    options={autocompleteOptions}
                    freeSolo={false}
                  />
                )}
              </>
            ) : (
              <>
                <Alert severity='warning'>{t('editOldWarning')}</Alert>
                <br />
                <TextInput
                  source='name'
                  label={t('name')}
                  fullWidth
                  disabled={disabled}
                  validate={required()}
                />
                <ReferenceInput source='productId' reference='products' filter={{ solutionId }}>
                  <SelectInput optionText='name' validate={required()} />
                </ReferenceInput>
                <DateInput
                  source='activeFrom'
                  label={t('activeFrom')}
                  options={{ format: 'LL', clearable: true }}
                  fullWidth
                  disabled={disabled}
                />
                <DateInput
                  source='activeTill'
                  label={t('activeTill')}
                  options={{ format: 'LL', clearable: true }}
                  fullWidth
                  disabled={disabled}
                />
                <SelectInput
                  source='accountStatus'
                  label={t('accountStatus')}
                  choices={[
                    { id: 'ACTIVE', name: t('Active') },
                    { id: 'BLOCKED', name: t('Blocked') },
                    { id: 'CLOSED', name: t('Closed') },
                  ]}
                  fullWidth
                  disabled={disabled}
                  allowEmpty
                  emptyText={t('any')}
                />
                <KycLevelInput
                  source='kycLevel'
                  label={t('kycLevel')}
                  fullWidth
                  disabled={disabled}
                  allowEmpty
                />
                {allTagsLoading ? (
                  <Skeleton width='100%' />
                ) : (
                  <AutocompleteArrayTextInput
                    record={pricingRecord}
                    source='profileTags'
                    label={t('profileTags')}
                    options={autocompleteOptions}
                    freeSolo={false}
                  />
                )}
              </>
            )}
          </Fragment>
        );
      }}
    </FormDataConsumer>
  );
};

export default PricingForm;
